import React from "react";
import { Link } from "react-router-dom";
import { guidesEs } from "@/data/guidesEs";
import Header from "@/components/Header";
import EsFooter from "@/components/EsFooter";
import VideoEmbed from "@/components/VideoEmbed";
import AppStoreButton from "@/components/ui/AppStoreButton";
import { APP_STORE_URL, PLAY_STORE_URL } from "@/seo/site";
import { useDocumentMeta } from "@/seo/useDocumentMeta";
import { esAudiences, esFaqs, esFeatures, esSteps } from "@/data/es";

const StoreButtons = () => (
  <div className="flex gap-4 justify-center lg:justify-start items-center flex-wrap">
    <AppStoreButton type="google" url={PLAY_STORE_URL} />
    <AppStoreButton type="apple" url={APP_STORE_URL} />
  </div>
);

/**
 * Spanish landing page. Uses the shared Header in Spanish (its menu scrolls to
 * the section ids below) and the Spanish footer, since the English footer
 * links to English homepage sections.
 */
const EsHome = () => {
  useDocumentMeta("/es");

  return (
    <div className="min-h-screen bg-white" lang="es">
      <Header lang="es" />

      <main>
        <section className="container mx-auto px-4 pt-32 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <span className="inline-block px-4 py-2 mb-6 bg-reelmatch-primary text-reelmatch-dark rounded-full text-sm font-medium">
                Movie matcher gratis para iPhone y Android
              </span>
              <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
                ReelMatch: la app para elegir qué película ver en pareja, con
                amigos o en familia
              </h1>
              <p className="text-xl text-reelmatch-gray mb-8">
                Deja de discutir qué ver. Cada quien desliza tráilers en su
                teléfono y ReelMatch les muestra las películas y series que a
                todos les gustan.
              </p>
              <StoreButtons />
            </div>
            <figure className="relative mx-auto max-w-xs">
              <div className="aspect-[9/19] rounded-[2.5rem] border-8 border-reelmatch-black overflow-hidden shadow-elevated bg-reelmatch-black">
                <picture>
                  <source
                    srcSet="/images/reelmatch_home_he-man.webp"
                    type="image/webp"
                  />
                  <img
                    src="/images/reelmatch_home_he-man.png"
                    alt="La app ReelMatch mostrando un tráiler para deslizar"
                    width="400"
                    height="890"
                    loading="eager"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                </picture>
              </div>
            </figure>
          </div>
        </section>

        <section id="como-funciona" className="bg-reelmatch-secondary/30">
          <div className="container mx-auto px-4 py-20">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">
              Cómo funciona
            </h2>
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {esSteps.map((step, index) => (
                <li
                  key={step.title}
                  className="p-6 rounded-2xl bg-white shadow-subtle text-center"
                >
                  <div
                    className="w-12 h-12 mx-auto mb-4 bg-reelmatch-primary/10 rounded-full flex items-center justify-center font-bold text-reelmatch-dark"
                    aria-hidden="true"
                  >
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-reelmatch-dark">{step.description}</p>
                </li>
              ))}
            </ol>
            <h3 className="text-2xl font-display font-bold text-center mt-16 mb-6">
              Míralo en 26 segundos
            </h3>
            <VideoEmbed lang="es" />
          </div>
        </section>

        <section id="para-quien" className="container mx-auto px-4 py-20">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">
            Para quién es ReelMatch
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {esAudiences.map((audience) => (
              <div
                key={audience.title}
                className="p-6 rounded-2xl bg-white shadow-subtle"
              >
                <h3 className="text-xl font-bold mb-2">{audience.title}</h3>
                <p className="text-reelmatch-dark">{audience.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="por-que-trailers" className="container mx-auto px-4 pb-20 max-w-3xl">
          <h2 className="text-3xl font-display font-bold mb-6">
            ¿Por qué tráilers?
          </h2>
          <p className="text-lg text-reelmatch-dark mb-8">
            Casi siempre que no se ponen de acuerdo, en realidad es por el
            tono. Treinta segundos de tráiler lo aclaran más rápido que un
            póster o una sinopsis.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-reelmatch-dark">
            {esFeatures.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        </section>

        <section id="guias" className="container mx-auto px-4 pb-20 max-w-3xl">
          <h2 className="text-3xl font-display font-bold mb-6">
            Ideas para elegir qué ver
          </h2>
          <ul className="space-y-4">
            {guidesEs.map((guide) => (
              <li key={guide.slug}>
                <Link
                  to={`/es/guias/${guide.slug}`}
                  className="text-lg font-semibold underline underline-offset-4 hover:text-reelmatch-primary transition-colors"
                >
                  {guide.title}
                </Link>
                <p className="text-reelmatch-dark">{guide.description}</p>
              </li>
            ))}
          </ul>
        </section>

        <section id="preguntas" className="bg-reelmatch-secondary/30">
          <div className="container mx-auto px-4 py-20 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-10">
              Preguntas frecuentes
            </h2>
            <div className="space-y-6">
              {esFaqs.map((faq) => (
                <div key={faq.question}>
                  <h3 className="text-lg font-bold mb-2">{faq.question}</h3>
                  <p className="text-reelmatch-dark">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="descargar" className="container mx-auto px-4 py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
            Deja de buscar. Empieza a ver.
          </h2>
          <p className="text-lg text-reelmatch-dark mb-8">
            ReelMatch es gratis en iPhone y Android.
          </p>
          <div className="flex justify-center">
            <StoreButtons />
          </div>
        </section>
      </main>

      <EsFooter />
    </div>
  );
};

export default EsHome;
